import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import { rfqMyQuotesPaginationStyles } from "@insite/content-library/Widgets/RfqMyQuotes/RfqMyQuotesPagination";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Filter from "@insite/mobius/Icons/Filter";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Pagination from "@insite/mobius/Pagination";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomSmallHeadingAndText, { SmallHeadingAndTextStyles } from "@WhiteCap/Components/CustomSmallHeadingAndText";
import StatusLableAndHeading from "@WhiteCap/Components/StatusLableAndHeading";
import getQuoteList from "@WhiteCap/Handlers/OsnQuotes/GetQuoteList";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import QuoteKebabMenu from "@WhiteCap/Widgets/OsnQuotes/QuoteKebabMenu";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const queryObj = parseQueryString<{
        quoteNumber?: string;
        createdStartDate?: string;
        createdEndDate?: string;
        expiredStartDate?: string;
        expiredEndDate?: string;
        jobsite?: string;
        branchContact?: string;
    }>(location.search);

    return {
        isLoading: state.WhiteCap.osnQuotes.isLoading,
        quotes: state.WhiteCap.osnQuotes.quoteData?.openQuotes,
        pagination: {
            pageNumber: state.WhiteCap.osnQuotes.quoteData.pageNumber,
            pageSize: state.WhiteCap.osnQuotes.quoteData.pageSize,
            pageSizeOptions: [8, 16, 24, 32],
            totalItemCount: state.WhiteCap.osnQuotes.quoteData.totalQuotes,
        },
        session: state.context.session,
        queryObj,
        billTosById: state.data.billTos.byId,
        osnQuoteDetailsPageUrl: getPageLinkByPageType(state, "OsnQuoteDetailsPage")?.url,
        osnEditQuotePageUrl: getPageLinkByPageType(state, "OsnEditQuotePage")?.url,
    };
};

const mapDispatchToProps = {
    getQuoteList,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface OrderHistoryTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    quoteDateCells?: DataTableCellProps;
    kebabMenuCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    dataTable?: DataTableProps;
    headingAndText?: SmallHeadingAndTextStyles;
    headingAndTextLeftAligned?: SmallHeadingAndTextStyles;
    kababMenu?: DataTableCellProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    headerContainer?: InjectableCss;
    totalCount?: TypographyProps;
    searchText?: TypographyProps;
    // Mobile Styles
    tileWrapper?: InjectableCss;
    tileSection?: InjectableCss;
    tileSectionWithBorder?: InjectableCss;
    headingAndTextMobile?: SmallHeadingAndTextStyles;
    deliverToHeadingAndText?: SmallHeadingAndTextStyles;
    deliverToHeadingAndTextMobile?: SmallHeadingAndTextStyles;
}

export const orderHistoryTableStyles: OrderHistoryTableStyles = {
    container: {},
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    kebabMenuCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    kababMenu: {
        css: css`
            text-align: right;
            padding: 0px;
            width: 100%;
        `,
    },
    headingAndText: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                text-align: center;
                font-weight: 400;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                text-align: center;
                font-weight: 500;
            `,
        },
    },
    headingAndTextLeftAligned: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 400;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 500;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 10px !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    deliverToHeadingAndText: {
        text: {
            css: css`
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 300px;
            `,
        },
    },
    deliverToHeadingAndTextMobile: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 400;
                flex-shrink: 0;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 500;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 10px !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 10px;
            `,
        },
    },
    headingAndTextMobile: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                text-align: center;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                flex-shrink: 0;
                text-align: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 10px !important;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    tileWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
            margin-bottom: 10px;
            &:nth-child(odd) {
                background: #f7f8f9;
            }
        `,
    },
    tileSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
        `,
    },
    tileSectionWithBorder: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-top: 2px solid white;
            border-bottom: 2px solid white;
            align-items: center;
            padding: 12px;
        `,
    },
    headerContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 8px;
                        `,
                        null,
                        css`
                            margin-bottom: 16px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    toggleFilterButton: {
        color: "common.background",
        shape: "pill",
        buttonType: "solid",
        shadow: false,
        hoverMode: "none",
        css: css`
            width: auto;
            padding: 0 12px;
            display: flex;
            align-items: center;
            gap: 8px;
            white-space: nowrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        span[data-test-selector="orderHistory_showHideFiltersText"] {
                            display: none;
                        }
                    `,
                    css`
                        span[data-test-selector="orderHistory_showHideFiltersText"] {
                            display: none;
                        }
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
    totalCount: {
        weight: 400,
        lineHeight: "normal",
        color: "text.main",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 12px;
                        `,
                        null,
                        css`
                            font-size: 14px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    searchText: {
        color: "#000",
        size: 14,
        weight: 400,
        lineHeight: "normal",
    },
};

const styles = orderHistoryTableStyles;

export const getStatusMessage = (status: string) => {
    switch (status?.toLowerCase()) {
        case "draft":
            return siteMessage("OSN_Quotes_Status_Draft").toString();
        case "closed":
            return siteMessage("OSN_Quotes_Status_Closed").toString();
        case "booked":
            return siteMessage("OSN_Quotes_Status_Booked").toString();
        default:
            return siteMessage("OSN_Quotes_Status_Draft").toString();
    }
};

const QuoteList: React.FC<Props> = ({
    id,
    getQuoteList,
    pagination,
    isLoading,
    quotes,
    session,
    queryObj,
    billTosById,
    osnQuoteDetailsPageUrl,
    osnEditQuotePageUrl,
}) => {
    const billToId = session.billToId;
    const billToDetails = billToId ? billTosById[billToId] : null;
    const toasterContext = useContext(ToasterContext);
    const dispatch = useDispatch();
    const noSearchResultMsg = siteMessage("OSN_Quote_List_No_Search_Result_Message");

    const handlePageChange = (pageNumber: number) => {
        if (pagination.pageNumber === pageNumber || !billToDetails) {
            return;
        }
        getQuoteList({
            pageNumber,
            pageSize: pagination.pageSize,
            customerNumber: billToDetails?.customerNumber || "",
            ...queryObj,
            onError(message) {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
        });
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        getQuoteList({
            pageNumber: 1,
            pageSize: +value,
            customerNumber: billToDetails?.customerNumber || "",
            ...queryObj,
            onError(message) {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
            },
        });
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.headerContainer}>
                <Typography {...styles.totalCount}>
                    {pagination.totalItemCount > 1
                        ? `${pagination.totalItemCount} Quotes`
                        : pagination.totalItemCount === 1
                        ? "Quote"
                        : ""}
                </Typography>

                <Button
                    {...styles.toggleFilterButton}
                    onClick={() => {
                        dispatch({
                            type: "Custom/OsnQuoteList/ToggleFiltersOpenState",
                        });
                    }}
                >
                    <ButtonIcon {...styles.toggleFilterButtonIcon} />
                    <Typography data-test-selector="orderHistory_showHideFiltersText">Search & Filter</Typography>
                </Button>
            </StyledWrapper>
            <Zone contentId={id} zoneName="Content" />
            {isLoading || !billToDetails ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : quotes && quotes.length > 0 ? (
                <StyledWrapper>
                    <Hidden
                        below="md"
                        css={css`
                            width: 100%;
                        `}
                    >
                        <DataTable {...styles.dataTable}>
                            <DataTableBody>
                                {quotes?.map(
                                    (
                                        {
                                            quoteNumber,
                                            status,
                                            createdOn,
                                            expiresOn,
                                            items,
                                            total,
                                            totalDisplay,
                                            jobsite,
                                        },
                                        index,
                                    ) => (
                                        <DataTableRow key={`${index}${{ quoteNumber }}`}>
                                            <DataTableCell
                                                {...styles.quoteDateCells}
                                                data-test-selector="orderHistoryTable_tableCell_date"
                                            >
                                                {quoteNumber && (
                                                    <CustomSmallHeadingAndText
                                                        // eslint-disable-next-line spire/avoid-dynamic-translate
                                                        heading={translate("QUOTE #")}
                                                        text={quoteNumber}
                                                        extendedStyles={styles.headingAndText}
                                                        isOsnQuoteListPage={true}
                                                        osnQuoteDetailsPageUrl={osnQuoteDetailsPageUrl}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.statusCell}
                                                data-test-selector="orderHistoryTable_tableCell_status"
                                            >
                                                {status && (
                                                    <StatusLableAndHeading
                                                        heading={translate("STATUS")}
                                                        text={status}
                                                        extendedStyles={styles.headingAndText}
                                                        isQuoteListPage={true}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...styles.headingAndText}>
                                                {createdOn && (
                                                    <SmallHeadingAndText
                                                        heading={translate("CREATED ON")}
                                                        text={createdOn}
                                                        extendedStyles={styles.headingAndText}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...styles.headingAndText}>
                                                {expiresOn && (
                                                    <SmallHeadingAndText
                                                        heading={translate("EXPIRES ON")}
                                                        text={expiresOn}
                                                        extendedStyles={styles.headingAndText}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...styles.headingAndText}>
                                                {items && (
                                                    <SmallHeadingAndText
                                                        heading={translate("ITEMS")}
                                                        text={items}
                                                        extendedStyles={styles.headingAndText}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...styles.headingAndText}>
                                                <SmallHeadingAndText
                                                    heading={translate("TOTAL")}
                                                    text={totalDisplay || "$0.00"}
                                                    extendedStyles={styles.headingAndText}
                                                />
                                            </DataTableCell>
                                            <DataTableCell {...styles.headingAndText}>
                                                {jobsite && (
                                                    <SmallHeadingAndText
                                                        heading={translate("DELIVER-TO / JOBSITE")}
                                                        text={jobsite}
                                                        extendedStyles={styles.deliverToHeadingAndText}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell {...styles.kebabMenuCell}>
                                                <QuoteKebabMenu
                                                    quoteNumber={quoteNumber}
                                                    osnQuoteDetailsPageUrl={osnQuoteDetailsPageUrl}
                                                    osnEditQuotePageUrl={osnEditQuotePageUrl}
                                                    status={status}
                                                />
                                            </DataTableCell>
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </Hidden>
                    {/* below is the mobile. */}
                    <Hidden above="sm">
                        {quotes.map(
                            ({ quoteNumber, status, createdOn, expiresOn, items, jobsite, totalDisplay }, index) => (
                                <StyledWrapper key={quoteNumber} {...styles.tileWrapper}>
                                    <StyledWrapper {...styles.tileSection}>
                                        <StyledWrapper>
                                            <SmallHeadingAndText
                                                heading={translate("QUOTE #")}
                                                text={quoteNumber}
                                                extendedStyles={styles.headingAndTextLeftAligned}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <SmallHeadingAndText
                                                heading={translate("ITEMS")}
                                                text={items}
                                                extendedStyles={styles.headingAndTextMobile}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <SmallHeadingAndText
                                                heading={translate("TOTAL")}
                                                text={totalDisplay}
                                                extendedStyles={styles.headingAndTextMobile}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <QuoteKebabMenu
                                                quoteNumber={quoteNumber}
                                                osnQuoteDetailsPageUrl={osnQuoteDetailsPageUrl}
                                                osnEditQuotePageUrl={osnEditQuotePageUrl}
                                                status={status}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.tileSectionWithBorder}>
                                        <StyledWrapper>
                                            <SmallHeadingAndText
                                                heading={translate("CREATED ON")}
                                                text={createdOn}
                                                extendedStyles={styles.headingAndTextLeftAligned}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <SmallHeadingAndText
                                                heading={translate("EXPIRES ON")}
                                                text={expiresOn}
                                                extendedStyles={styles.headingAndTextMobile}
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <StatusLableAndHeading
                                                heading={translate("STATUS")}
                                                text={status}
                                                extendedStyles={styles.headingAndTextMobile}
                                                isQuoteListPage={true}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.tileSection}>
                                        <SmallHeadingAndText
                                            heading={translate("Deliver-To")}
                                            text={jobsite}
                                            extendedStyles={styles.deliverToHeadingAndTextMobile}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            ),
                        )}
                    </Hidden>
                    {pagination.totalItemCount > 0 && (
                        <Pagination
                            {...rfqMyQuotesPaginationStyles.pagination}
                            resultsCount={pagination.totalItemCount}
                            currentPage={pagination.pageNumber}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            onChangePage={handlePageChange}
                            onChangeResultsPerPage={handlePageSizeChange}
                        />
                    )}
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText}>
                        {noSearchResultMsg}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(QuoteList))),
    definition: {
        group: "OSN Quotes" as any,
        displayName: "OSN Quote List",
        allowedContexts: ["OsnQuoteListPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
